import React, { useEffect, useState } from 'react';
import { Formik, Form, FieldArray, ErrorMessage } from 'formik';
import { ExtraStorageFormSchema } from './extraStorageSchema';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { updateExtraStorage } from '@custom/rtk-apis/Setting/extraStorage/updateExtraStorage';
import { getExtraStorage } from '@custom/rtk-apis/Setting/extraStorage/getExtraStorage';
import { resetSuccess as resetUpdateExtraStorageSuccess } from '@custom/store/slices/Setting/extraStorage/updateExtraStorageSlice';
import { Loader, SuccessModal, useTheme } from '@ohif/ui';
import './styles.css';
import Header from '@custom/Header/Header';
import { customStyle } from '@custom/Utils/utils';
import { _routes } from '@custom/Utils/constants/routes';
import { useNavigate } from 'react-router-dom';

interface FeaturePoint {
  point: string;
}

export interface ExtraStoragePlan {
  _id?: string;
  planName: string;
  frequency: number;
  creditsPerGB: number;
}

const emptyPlan: ExtraStoragePlan = {
  planName: '',
  frequency: 0,
  creditsPerGB: 0,
};

const ExtraStorageForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const getExtraStorageResponse = useSelector((state: RootState) => state?.getExtraStorageResponse);
  const updateExtraStorageResponse = useSelector((state: RootState) => state?.updateExtraStorageResponse);

  const [formPlans, setFormPlans] = useState<ExtraStoragePlan[]>([emptyPlan]);
  const [successModal, setSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const isDarkMode = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    dispatch(getExtraStorage());
  }, [dispatch]);

  useEffect(() => {
    if (getExtraStorageResponse?.success) {
      setIsLoading(false);
      if (getExtraStorageResponse?.result?.plans?.length > 0) {
        setFormPlans(getExtraStorageResponse?.result?.plans);
      } else {
        setFormPlans([emptyPlan]);
      }
    } else {
      setIsLoading(false);
      setFormPlans([emptyPlan]);
    }
  }, [getExtraStorageResponse]);

  useEffect(() => {
    if (updateExtraStorageResponse?.success) {
      setSuccessModal(true);
      setTimeout(() => {
        setSuccessModal(false);
        dispatch(resetUpdateExtraStorageSuccess());
        dispatch(getExtraStorage());
        navigate(_routes.settings.pricing);
      }, 2000);
    }
  }, [updateExtraStorageResponse, dispatch]);

  const handleSubmit = (values: { extraStorages: ExtraStoragePlan[] }) => {
    setIsLoading(true);
    dispatch(updateExtraStorage(values.extraStorages))
      .unwrap()
      .catch((error) => {
        console.error('Error updating extra storage plans:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="page page__full" style={
      isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
    }>
      <Header title="Plan Settings" />
      <div className="page__body">
        <div className="btn__group custom__plan__btn" style={{ justifyContent: 'flex-end', alignItems: 'center' }}>
          <button
            className="btn btn__white"
            onClick={() => {
              navigate(_routes.settings.pricing);
            }}
          >
            Preview credits plan
          </button>
        </div>
        <div className="details">
          <div className="form form--innersec">
            <section className="form__section">
              <h2 className="form__section__title">Extra Storage Settings</h2>
              <Formik
                initialValues={{ extraStorages: formPlans }}
                validationSchema={ExtraStorageFormSchema}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, errors, handleChange }) => (
                  <Form>
                    <FieldArray name="extraStorages">
                      {({ remove: removePlan, push: pushPlan }) => (
                        <>
                          {values?.extraStorages?.map((plan, planIndex) => (
                            <div key={planIndex}>
                              {/* Remove plan button */}
                              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                                <button
                                  type="button"
                                  className="btn btn__danger"
                                  disabled={values.extraStorages.length === 1}
                                  onClick={() => removePlan(planIndex)}
                                >
                                  Remove
                                </button>
                              </div>
                              <div className="formRow formRow__3">
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label" htmlFor='hospitalName'>
                                      Plan Name
                                      <span className="required-asterisk">*</span>
                                    </label>
                                    <input
                                      className="form__input form__input--sm"
                                      type="text"
                                      name={`extraStorages.${planIndex}.planName`}
                                      value={plan.planName}
                                      onChange={handleChange(`extraStorages.${planIndex}.planName`)}
                                    />
                                    <ErrorMessage name={`extraStorages.${planIndex}.planName`} component="div" className="error text-error" />
                                  </div>
                                </div>
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label" htmlFor='hospitalName'>
                                      Frequency(No. of months)
                                      <span className="required-asterisk">*</span>
                                    </label>
                                    <input
                                      className="form__input form__input--sm"
                                      type="number"
                                      name={`extraStorages.${planIndex}.frequency`}
                                      value={plan.frequency}
                                      onChange={handleChange(`extraStorages.${planIndex}.frequency`)}
                                    />
                                    <ErrorMessage name={`extraStorages.${planIndex}.frequency`} component="div" className="error text-error" />
                                  </div>
                                </div>
                                <div className="formCol">
                                  <div className="form__Field">
                                    <label className="form__label" htmlFor='hospitalName'>
                                      Credits per GB
                                      <span className="required-asterisk">*</span>
                                    </label>
                                    <input
                                      className="form__input form__input--sm"
                                      type="number"
                                      name={`extraStorages.${planIndex}.creditsPerGB`}
                                      value={plan.creditsPerGB}
                                      onChange={handleChange(`extraStorages.${planIndex}.creditsPerGB`)}
                                    />
                                    <ErrorMessage name={`extraStorages.${planIndex}.creditsPerGB`} component="div" className="error text-error" />
                                  </div>
                                </div>
                              </div>
                              {planIndex < values.extraStorages.length - 1 && <hr style={{ margin: '2rem 0' }} />}
                            </div>
                          ))}
                        <div className="add-plan-button-container">
                          <button type="button" className="btn btn__secondary add-more-btn" onClick={() => pushPlan(emptyPlan)}>
                            + Add More
                          </button>
                        </div>
                        </>
                      )}
                    </FieldArray>

                    <div style={{ marginTop: '2rem' }}>
                      <button type="submit" className="btn btn__primary" disabled={isLoading}>
                        {isLoading ? 'Updating...' : 'Update Extra Storage'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
              <Loader isLoaderOpen={isLoading} />
              <SuccessModal
                isSuccessModalOpen={successModal}
                title="Extra Storage Updated Successfully!"
                handleCloseModal={() => setSuccessModal(false)}
              />
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtraStorageForm;
